/**
 * 统一 fetch 封装（见方案 4.6、5.2）。
 *
 * 三条铁律：
 *  1. **Access Token 只存在内存里**（模块级变量）。绝不写 localStorage/sessionStorage，
 *     避免 XSS 时被直接读走；页面刷新后靠 HttpOnly 的 Refresh Cookie 静默恢复。
 *  2. `credentials: 'include'` —— Refresh Cookie 是 HttpOnly Cookie，不带它就不会发送。
 *  3. 后端 `{ok:false,error:{code,message}}` 一律转成抛出的 `ApiError`，
 *     调用处只认异常，不用每次都判断 `res.ok` 字段。
 */
import { ERROR_CODES, type ErrorCode } from '@fht/shared';
import { ApiError, NetworkError, SessionExpiredError, isNotImplemented } from './error';
import type { SessionResponse } from './types';

/** 后端所有接口都挂在 /api/v1 下（见 api/src/app.ts），开发期由 Vite 代理。 */
export const API_PREFIX = '/api/v1';

const AUTH_ENDPOINTS = {
  login: '/auth/login',
  refresh: '/auth/refresh',
  logout: '/auth/logout',
  me: '/auth/me',
  captcha: '/auth/captcha',
} as const;

// ---------------------------------------------------------------- 会话状态（仅内存）

let accessToken: string | null = null;
/** 未登录时为 null；由 auth.tsx 维护。 */
let currentRole: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setCurrentRole(role: string | null): void {
  currentRole = role;
}

export function getCurrentRole(): string | null {
  return currentRole;
}

// ---------------------------------------------------------------- 会话事件（供 auth.tsx 订阅）

export type SessionEvent = 'session-expired' | 'session-refreshed' | 'signed-out';

type Listener = (event: SessionEvent) => void;
const listeners = new Set<Listener>();

/** 订阅会话变化；返回取消订阅函数。 */
export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: SessionEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (err) {
      console.error('[session] 事件处理器异常', err);
    }
  }
}

// ---------------------------------------------------------------- 请求参数

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** 是否附带 Authorization 头，默认 true。 */
  auth?: boolean;
  /** 内部使用：标记这是重试，避免 401 无限循环。 */
  retried?: boolean;
  /** 内部使用：刷新令牌请求不走 401 自动刷新。 */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  /** 额外的请求头（如写操作的 Idempotency-Key，见方案 4.6）。 */
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_PREFIX}${path}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

/** 尝试解析响应体；后端异常时可能返回 HTML（如反代 502），不能让 JSON.parse 把原始错误盖掉。 */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** 按 HTTP 状态给一句**能看懂**的中文兜底（仅在前两种取不到文案时使用）。 */
function statusFallbackMessage(status: number): string {
  switch (status) {
    case 400:
      return '提交的内容有误，请检查后重试';
    case 401:
      return '登录状态已失效，请重新登录';
    case 403:
      return '你没有权限执行此操作';
    case 404:
      return '未找到对应的数据';
    case 409:
      return '操作与当前数据状态冲突，请刷新页面后重试';
    case 413:
      return '内容太大了';
    case 429:
      return '操作太频繁，请稍后再试';
    case 500:
    case 502:
    case 503:
      return '服务器开小差了，请稍后再试';
    default:
      return `请求失败（HTTP ${status}）`;
  }
}

/**
 * 从失败的响应里取出错误码与**给用户看的**文案。
 *
 * 要兼容两种错误体：
 *  1. 本应用自己的信封：`{ ok:false, error:{ code, message, details } }`
 *  2. Fastify 的默认错误体：`{ statusCode, error: "Conflict", message: "..." }`
 *     —— 注意这里的 `error` 是**字符串**，所以 `envelope.error.message` 取不到东西。
 *
 * 为什么必须兼容第 2 种：真实发生过一次，某个 409 不是由本应用的错误处理器
 * 产生的，于是响应体是 Fastify 的默认形状；前端读不到 message，只能显示
 * 「请求失败（HTTP 409）」—— 用户完全看不出**为什么**失败（实际是"这条打卡任务
 * 已经有打卡记录，不能删除"）。宁可展示上游给的原文，也不要扔一句无信息量的兜底。
 */
function pickError(parsed: unknown, fallbackStatus: number): ApiError {
  const raw = (parsed ?? {}) as {
    ok?: false;
    error?: { code?: string; message?: string; details?: unknown } | string;
    message?: string;
    statusCode?: number;
  };

  const envelopeError = typeof raw.error === 'object' && raw.error !== null ? raw.error : undefined;

  const code = (envelopeError?.code ?? ERROR_CODES.INTERNAL) as ErrorCode;

  const message =
    envelopeError?.message ??
    // Fastify 默认错误体：message 在顶层
    (typeof raw.message === 'string' && raw.message ? raw.message : undefined) ??
    statusFallbackMessage(fallbackStatus);

  return new ApiError(code, message, fallbackStatus, envelopeError?.details);
}

// ---------------------------------------------------------------- 刷新令牌（单飞）

let refreshInFlight: Promise<SessionResponse | null> | null = null;

/**
 * 用 HttpOnly Cookie 换新的 Access Token。
 * 单飞（single-flight）：并发多个 401 时只发一次 refresh，避免把令牌轮换用乱
 * —— 后端对重复使用的 Refresh Token 会判定为令牌泄漏并吊销全部会话（见 auth.routes.ts）。
 */
export function refreshSession(): Promise<SessionResponse | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<SessionResponse | null> => {
    try {
      const res = await fetch(buildUrl(AUTH_ENDPOINTS.refresh), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const parsed = await readBody(res);
      if (!res.ok || !parsed || (parsed as { ok?: boolean }).ok !== true) {
        return null;
      }
      const session = (parsed as { data: SessionResponse }).data;
      if (!session?.accessToken) return null;
      accessToken = session.accessToken;
      emit('session-refreshed');
      return session;
    } catch {
      // 网络不可达：当作"暂时拿不到会话"，不要清空本地状态
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** 清空内存中的会话并通知上层（auth.tsx 会把 user 置空并跳登录页）。 */
export function clearSession(): void {
  accessToken = null;
  currentRole = null;
  emit('session-expired');
}

// ---------------------------------------------------------------- 核心请求

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, retried = false, skipRefresh = false } =
    options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  // 调用方显式传的头优先级最高（Idempotency-Key 必须原样透传）；
  // 但不允许覆盖 Authorization/Accept，避免调用方误把会话头写坏。
  for (const [key, value] of Object.entries(options.headers ?? {})) {
    const lower = key.toLowerCase();
    if (lower === 'authorization' || lower === 'accept') continue;
    headers[key] = value;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }

  // 401：Access Token 过期 → 静默刷新一次再重试原请求
  if (res.status === 401 && !skipRefresh && !retried && auth) {
    const session = await refreshSession();
    if (session) {
      return request<T>(path, { ...options, retried: true });
    }
    clearSession();
    throw new SessionExpiredError();
  }

  const parsed = await readBody(res);

  if (!res.ok) {
    throw pickError(parsed, res.status);
  }

  if (parsed === null) {
    // 204 或空响应：调用处不需要返回值的接口（如 logout）
    return undefined as T;
  }

  const envelope = parsed as { ok?: boolean; data?: T; error?: unknown };
  if (envelope.ok === false) {
    throw pickError(parsed, res.status);
  }
  // 后端统一用 {ok:true,data:...} 包装；没包装时直接把解析结果返回，容错旧接口
  return (envelope.ok === true ? (envelope.data as T) : (parsed as T));
}

// ---------------------------------------------------------------- 便捷方法

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'GET', ...(query ? { query } : {}) }),

  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),

  /**
   * `PUT`：语义是**整体替换**（"这一天的作业应该长这样"），
   * 见 `lib/api/homework.ts` 的整单保存。`RequestOptions` 里本来就支持它，
   * 这里补一个便捷方法，免得调用方绕过 `api` 直接写 `request()`。
   */
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  del: <T>(path: string, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * 生成幂等键（见方案 4.6）。
 *
 * 用法要点：**一次用户意图只生成一次**，然后在整个重试链上复用它。
 * 如果在每次 fetch 之前都新生成一个，弱网重试就会变成"两次不同的请求"，
 * 后端的幂等保护彻底失效（打卡重复加分、连点兑换超兑都由此而来）。
 * 因此调用处要把 key 放进 mutation 变量或 ref 里，而不是写在 fetcher 内部。
 */
export function newIdempotencyKey(scope: string): string {
  const random =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${scope}:${random}`;
}

/**
 * 下载类接口（CSV / xlsx 导出，见方案 4.5 `/stats/export`）。
 *
 * 为什么不复用 `request()`：那个函数假定响应是 JSON（要 readBody + 拆 {ok,data} 信封）。
 * 导出返回的是文件流，强行 JSON.parse 会把内容吃掉。
 * 这里保留同样的一条 401 静默刷新重试逻辑，其余照原样把 Response 交回调用方。
 */
export async function download(
  path: string,
  query?: Record<string, QueryValue>,
): Promise<Response> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: '*/*' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    try {
      return await fetch(buildUrl(path, query), { method: 'GET', credentials: 'include', headers });
    } catch {
      throw new NetworkError();
    }
  };

  let res = await send();
  if (res.status === 401) {
    const session = await refreshSession();
    if (session) {
      res = await send();
    } else {
      clearSession();
      throw new SessionExpiredError();
    }
  }
  return res;
}

/**
 * 三态请求：把"接口还没做"与"接口可用了但数据是空的"分开。
 *
 * 为什么 `withFallback` 不够：`withFallback` 把 404 与"空列表"压成同一个值，
 * 页面于是无法区分"还没有奖品（点这里添加）"和"奖品功能尚未就绪"。
 * 这两种文案对用户完全不同，所以本批新页面统一用这个函数。
 *
 * 返回：
 *  - `{ state: 'ok', data }`      接口正常（数据可以是空数组）
 *  - `{ state: 'notReady' }`      接口不存在（404/501/502/503）→ 显示"该功能尚未就绪"
 *  - 其它错误（401/403/网络/500）**原样抛出**，由页面显示错误态，绝不吞掉权限错误
 */
export type TriState<T> = { state: 'ok'; data: T } | { state: 'notReady' };

export async function tryRequest<T>(fn: () => Promise<T>): Promise<TriState<T>> {
  try {
    return { state: 'ok', data: await fn() };
  } catch (err) {
    if (isNotImplemented(err)) return { state: 'notReady' };
    throw err;
  }
}

/**
 * 优雅降级：接口还没实现（404/501）时返回兜底值，而不是让整个页面报错。
 * 打卡 / 积分等模块尚未落地时靠它保证界面可用（见任务说明）。
 */
export async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isNotImplemented(err)) return fallback;
    throw err;
  }
}

export { AUTH_ENDPOINTS };
